import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { GroupService } from '../../services/group.service';
import { ProjectService } from '../../services/project.service';
import { ProfileService } from '../../services/profile.service';
import { NotificationService } from '../../services/notification.service';
import { Member } from '../../dtos/member';
import { JoinRequest } from '../../dtos/join-request';
import { Project } from '../../dtos/project';
import { UserInfo } from '../../dtos/user-info';
import { PaginatedResponse } from '../../dtos/paginated';
import { ProjectCardComponent } from '../projects/project-card/project-card.component';
import { BaseModalComponent } from '../modals/base-modal/base-modal.component';
import { ConfirmModalComponent } from '../modals/confirm-modal/confirm-modal.component';
import { ProjectCreateModalComponent } from '../modals/project-create-modal/project-create-modal.component';

const PAGE_SIZE = 15;
const DEBOUNCE_MS = 300;

@Component({
  selector: 'app-group-overview',
  standalone: true,
  imports: [
    CommonModule,
    ProjectCardComponent,
    BaseModalComponent,
    ConfirmModalComponent,
    ProjectCreateModalComponent,
  ],
  templateUrl: './group-overview.component.html',
})
export class GroupOverviewComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private groupService = inject(GroupService);
  private projectService = inject(ProjectService);
  private profileService = inject(ProfileService);
  private notificationService = inject(NotificationService);

  readonly membersPage = signal<PaginatedResponse<Member> | null>(null);
  readonly currentUserRole = signal<string | null>(null);
  readonly currentPage = signal(0);
  readonly totalPages = signal(0);
  readonly searchQuery = signal('');
  private readonly search$ = new Subject<{ q: string; page: number }>();
  readonly joinRequests = signal<JoinRequest[]>([]);
  readonly recentProjects = signal<Project[]>([]);
  readonly projectCount = signal(0);
  readonly loading = signal(true);
  readonly isViewer = signal(false);
  readonly errorMessage = signal('');
  readonly showRequestsModal = signal(false);
  readonly showProjectCreateModal = signal(false);
  readonly showRemoveConfirm = signal(false);
  readonly processingRequestId = signal<number | null>(null);
  readonly removingMemberId = signal<string | null>(null);
  readonly deleting = signal(false);
  readonly memberToRemove = signal<Member | null>(null);

  readonly membersView = computed(() => this.membersPage()?.items ?? []);
  readonly totalMembers = computed(() => this.membersPage()?.totalItems ?? 0);

  groupId = '';
  private userInfo: UserInfo | null = null;
  private sub?: Subscription;
  private seenRequestNotificationIds = new Set<number>();
  private notificationsInitialized = false;

  ngOnInit(): void {
    const searchSub = this.search$
      .pipe(
        debounceTime(DEBOUNCE_MS),
        switchMap(({ q, page }) => {
          this.currentPage.set(page);
          return this.groupService.getGroupMembers(this.groupId, page, PAGE_SIZE, q);
        }),
      )
      .subscribe({
        next: (res) => {
          this.membersPage.set(res.page);
          this.currentUserRole.set(res.currentUserRole);
          this.currentPage.set(res.page.currentPage);
          this.totalPages.set(res.page.totalPages);
          this.loading.set(false);
        },
        error: (err) => {
          if (err?.status === 403) {
            this.isViewer.set(true);
          } else {
            this.errorMessage.set(
              err.error?.detail || 'Could not load group members. Please try again.',
            );
          }
          this.loading.set(false);
        },
      });

    this.sub = new Subscription();
    this.sub.add(searchSub);

    const paramsSub = this.route.parent?.params.subscribe((params) => {
      this.groupId = params['id'];
      if (this.groupId) {
        this.seenRequestNotificationIds.clear();
        this.notificationsInitialized = false;
        this.loadData();
      }
    });
    if (paramsSub) {
      this.sub.add(paramsSub);
    }

    this.sub.add(
      this.notificationService.allNotifications$.subscribe((notifications) => {
        if (!this.groupId) return;
        const forThisGroup = notifications.filter(
          (n) => n.type === 'GROUP_JOIN_REQUEST' && n.relatedGroupId === Number(this.groupId),
        );
        if (!this.notificationsInitialized) {
          forThisGroup.forEach((n) => this.seenRequestNotificationIds.add(n.id));
          this.notificationsInitialized = true;
          if (forThisGroup.length > 0) {
            this.loadJoinRequests();
          }
          return;
        }
        const unseen = forThisGroup.filter((n) => !this.seenRequestNotificationIds.has(n.id));
        if (unseen.length > 0) {
          unseen.forEach((n) => this.seenRequestNotificationIds.add(n.id));
          this.loadJoinRequests();
        }
      }),
    );

    this.loadUserInfo();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private loadData(): void {
    this.loading.set(true);
    this.errorMessage.set('');

    this.triggerSearch();

    this.loadJoinRequests();

    this.projectService.getProjectsPaginated(0, 3, undefined, this.groupId).subscribe({
      next: (res) => {
        this.projectCount.set(res.totalItems);
        this.recentProjects.set(res.items);
      },
      error: () => {},
    });
  }

  private loadJoinRequests(): void {
    this.groupService.getGroupJoinRequests(this.groupId).subscribe({
      next: (requests) => this.joinRequests.set(requests),
      error: () => {},
    });
  }

  private loadUserInfo(): void {
    this.profileService.getCurrentUser().subscribe({
      next: (user) => (this.userInfo = user),
      error: () => (this.userInfo = null),
    });
  }

  isOwner(): boolean {
    return this.currentUserRole() === 'OWNER';
  }

  approveRequest(request: JoinRequest): void {
    if (this.processingRequestId()) return;
    this.processingRequestId.set(request.id);
    this.groupService.approveJoinRequest(this.groupId, request.id, 'MEMBER').subscribe({
      next: () => {
        this.joinRequests.set(this.joinRequests().filter((r) => r.id !== request.id));
        this.processingRequestId.set(null);
        this.refetchCurrentPage();
      },
      error: (err) => {
        this.errorMessage.set(err.error?.detail || 'Could not approve request. Please try again.');
        this.processingRequestId.set(null);
      },
    });
  }

  rejectRequest(request: JoinRequest): void {
    if (this.processingRequestId()) return;
    this.processingRequestId.set(request.id);
    this.groupService.rejectJoinRequest(this.groupId, request.id).subscribe({
      next: () => {
        this.joinRequests.set(this.joinRequests().filter((r) => r.id !== request.id));
        this.processingRequestId.set(null);
      },
      error: () => {
        this.errorMessage.set('Could not reject request. Please try again.');
        this.processingRequestId.set(null);
      },
    });
  }

  removeMember(): void {
    if (this.removingMemberId() || !this.memberToRemove()) return;
    this.deleting.set(true);
    const userId = this.memberToRemove()!.user.id ?? '';
    this.removingMemberId.set(userId);
    this.groupService.removeMember(this.groupId, userId).subscribe({
      next: () => {
        this.removingMemberId.set(null);
        this.deleting.set(false);
        this.cancelRemove();
        this.refetchCurrentPage();
      },
      error: () => {
        this.errorMessage.set('Could not remove member. Please try again.');
        this.removingMemberId.set(null);
        this.deleting.set(false);
      },
    });
  }

  askRemove(member: Member): void {
    this.showRemoveConfirm.set(true);
    this.memberToRemove.set(member);
  }
  cancelRemove(): void {
    this.showRemoveConfirm.set(false);
    this.memberToRemove.set(null);
  }

  viewGroupProjects(): void {
    this.router.navigate(['/projects'], {
      queryParams: { group: this.groupId },
    });
  }

  openProjectCreateModal(): void {
    this.showProjectCreateModal.set(true);
  }

  onProjectCreated(project: Project): void {
    this.recentProjects.set([project, ...this.recentProjects()].slice(0, 3));
  }

  onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.currentPage.set(0);
    this.triggerSearch();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages()) return;
    this.currentPage.set(page);
    this.triggerSearch();
  }

  canPrev(): boolean {
    return this.currentPage() > 0;
  }

  canNext(): boolean {
    return this.currentPage() < this.totalPages() - 1;
  }

  private triggerSearch(): void {
    this.search$.next({ q: this.searchQuery(), page: this.currentPage() });
  }

  private refetchCurrentPage(): void {
    this.triggerSearch();
  }
}
